import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import DashboardRoute from './pages/DashboardRoute';
import FranchiseesPage from './pages/FranchiseesPage';
import AddFranchiseePage from './pages/AddFranchiseePage';
import AllowedEmailsPage from './pages/AllowedEmailsPage';
import ManageSupplierAccountsPage from './pages/ManageSupplierAccountsPage';
import TerritoriesPage from './pages/TerritoriesPage';
import ClientsPage from './pages/ClientsPage'; // This is for Suppliers (original Clients)
import AddClientPage from './pages/AddClientPage'; // This is for Suppliers (original Clients)
// Revert Household imports to match filenames
import HouseholdsPage from './pages/HouseholdsPage'; // Reverted import name
import AddHouseholdPage from './pages/AddHouseholdPage'; // Reverted import name
import JobSchedulerPage from './pages/JobSchedulerPage';
import JobsListPage from './pages/JobsListPage';
import AddJobSchedulePage from './pages/AddJobSchedulePage';
import ServiceTypesPage from './pages/ServiceTypesPage';
import SettingsPage from './pages/SettingsPage';
import EditFranchiseePage from './pages/EditFranchiseePage'; // Import the new edit page
import EditClientPage from './pages/EditClientPage'; // Import the new client edit page
import EditHouseholdPage from './pages/EditHouseholdPage'; // Import the new household edit page
import EditJobSchedulePage from './pages/EditJobSchedulePage'; // Import the new schedule edit page
import EditJobPage from './pages/EditJobPage'; // Import the new job edit page
import ImportXeroContactsPage from './pages/ImportXeroContactsPage'; // Import the new Xero contacts import page
import XeroContactDetailsPage from './pages/XeroContactDetailsPage'; // Import the Xero contact details page
import InvoicesPage from './pages/InvoicesPage'; // Import the new InvoicesPage
import InvoiceDetailsPage from './pages/InvoiceDetailsPage'; // Import the new InvoiceDetailsPage
import RCTIDetailsPage from './pages/RCTIDetailsPage'; // Import the new RCTIDetailsPage
import ChartOfAccountsPage from './pages/ChartOfAccountsPage'; // Import the new ChartOfAccountsPage
import UtilitiesPage from './pages/UtilitiesPage'; // Import the new UtilitiesPage
import ImportJobSchedulesHistoryPage from './pages/ImportJobSchedulesHistoryPage';
import ImportJobSchedulesHistoryDetailPage from './pages/ImportJobSchedulesHistoryDetailPage';
import ReportsPage from './pages/ReportsPage'; // Import the new ReportsPage
import ActivityLogPage from './pages/ActivityLogPage'; // Import the Activity Log page
import ForgotPasswordPage from './pages/ForgotPasswordPage'; // Import the new ForgotPasswordPage
import ChangePasswordPage from './pages/ChangePasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccountPage from './pages/AccountPage'; // Import the new AccountPage
import MessagingPage from './pages/MessagingPage'; // Import the Messaging page (ticket 4)
import EmailMonitorPage from './pages/EmailMonitorPage'; // Email Monitor module (split out of Messaging)
import SupplierPaymentsPage from './pages/SupplierPaymentsPage'; // Import the supplier payment matching page
import AdminUsersPage from './pages/AdminUsersPage'; // Import the super-admin admin-user management page
import { AppConfigProvider } from './context/AppConfigContext'; // Import the provider
import './firebase'; // Initialize Firebase

function App() {
  const isDevEnvironment = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'loope-dev-env';

  return (
    <AppConfigProvider> {/* Wrap the Router with the provider */}
      <Router>
        <div className="min-h-screen bg-gray-100">
          {/* Dev Environment Banner */}
          {isDevEnvironment && (
            <div className="bg-yellow-500 text-white text-center py-1 px-4 text-sm font-medium sticky top-0 z-50 shadow-md">
              🧪 TEST ENVIRONMENT - Development Mode
            </div>
          )}
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            {/* Inside ProtectedRoute on purpose: reaching it requires being signed in with the
                temporary password, and that same guard is what forces the visit. */}
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Old tile grid or new stats dashboard, chosen by the newDashboardEnabled flag (ticket 77). */}
              <Route path="/" element={<DashboardRoute />} />
              <Route path="/account" element={<AccountPage />} />
              {/* Franchisee Routes */}
              <Route path="/franchisees" element={<FranchiseesPage />} />
              <Route path="/franchisees/add" element={<AddFranchiseePage />} />
              <Route path="/franchisees/edit/:id" element={<EditFranchiseePage />} />
              <Route path="/franchisees/allowed-emails" element={<AllowedEmailsPage />} />
              {/* Manage Supplier Accounts (email allowlist only) */}
              <Route path="/suppliers/allowed-emails" element={<ManageSupplierAccountsPage />} />
              <Route path="/territories" element={<TerritoriesPage />} />
              {/* Client (Supplier) Routes - Keep path /clients */}
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/add" element={<AddClientPage />} />
              <Route path="/clients/edit/:id" element={<EditClientPage />} />
              {/* Client (formerly Household) Routes - Keep path /households for now */}
              <Route path="/households" element={<HouseholdsPage />} />
              <Route path="/households/add" element={<AddHouseholdPage />} />
              <Route path="/households/edit/:id" element={<EditHouseholdPage />} />
              {/* Job Scheduler Routes */}
              <Route path="/job-scheduler" element={<JobSchedulerPage />} />
              <Route path="/job-scheduler/add" element={<AddJobSchedulePage />} />
              <Route path="/job-scheduler/edit/:id" element={<EditJobSchedulePage />} />
              {/* Jobs List Route */}
              <Route path="/jobs-list" element={<JobsListPage />} />
              <Route path="/jobs-list/edit/:id" element={<EditJobPage />} />
              {/* Service Types Route */}
              <Route path="/services" element={<ServiceTypesPage />} />
              {/* Invoice Routes */}
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/invoices/:invoiceId" element={<InvoiceDetailsPage />} />
              {/* RCTI Routes */}
              <Route path="/rctis/:rctiId" element={<RCTIDetailsPage />} />
              {/* Chart of Accounts Route */}
              <Route path="/chart-of-accounts" element={<ChartOfAccountsPage />} />
              {/* Settings Route */}
              <Route path="/settings" element={<SettingsPage />} />
              {/* Utilities Route */}
              <Route path="/utilities" element={<UtilitiesPage />} />
              {/* Import Job Schedules History Routes */}
              <Route path="/utilities/import-job-schedules/history" element={<ImportJobSchedulesHistoryPage />} />
              <Route path="/utilities/import-job-schedules/history/:id" element={<ImportJobSchedulesHistoryDetailPage />} />
              {/* Activity Log Route */}
              <Route path="/activity-log" element={<ActivityLogPage />} />
              {/* Reports Route */}
              <Route path="/reports" element={<ReportsPage />} />
              {/* Xero Contacts Routes */}
              <Route path="/settings/xero-import-contacts" element={<ImportXeroContactsPage />} />
              <Route path="/settings/xero-contact-details/:contactId" element={<XeroContactDetailsPage />} />
              {/* Email Monitor module: inbound support mail + reply knowledge (split out of Messaging). */}
              <Route path="/email-monitor" element={<EmailMonitorPage />} />
              {/* Messaging Route (ticket 4): admin sends SMS/email to selected franchisees or broadcasts to all. */}
              <Route path="/messaging" element={<MessagingPage />} />
              {/* Supplier Payment Matching: upload remittance PDFs, match to Xero, apply payments */}
              <Route path="/supplier-payments" element={<SupplierPaymentsPage />} />
              {/* Admin Users: super-admin only. The page self-guards on the app config flag. */}
              <Route path="/admin-users" element={<AdminUsersPage />} />
            </Route>
          </Routes>
        </div>
      </Router>
    </AppConfigProvider>
  );
}

export default App;
